import dayjs from '../../utils/dayjs';
import { Button, DatePicker, IndexFilters, IndexFiltersProps, InlineStack, Link, Select, Text, useSetIndexFiltersMode } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';

import helpers from '../../helpers';
import useDidMountEffect from 'components/useDidMountEffect';
import __ from 'languages/index';
import { formatDateRange } from 'components/filter.helper';
import { ENUM_ORDER_STATUS } from 'queries/orders.query';
import { useDeleteUserMeta, useGetUserMeta, useUpdateUserMeta } from 'queries/user_meta.query';
import { ENUM_TRANSACTION_PAYMENT_METHOD } from 'queries/transactions.query';
import QuickSearchUser from 'components/quickSearchUser';
import { TypedUser } from 'interface/user.model';

const MONEY_COLLECTION_TYPE_OPTIONS = [
  { label: 'TẤT CẢ', value: '' },
  { label: 'Công nợ khách', value: ENUM_TRANSACTION_PAYMENT_METHOD.DEBT_CUSTOMER },
  { label: 'Công nợ đại lý', value: ENUM_TRANSACTION_PAYMENT_METHOD.DEBT_AGENT },
  { label: 'Công nợ bên thứ ba', value: ENUM_TRANSACTION_PAYMENT_METHOD.THIRD_PARTY },
];

const ORDER_STATUS_OPTIONS = [
  { label: 'TẤT CẢ', value: '' },
  { label: 'ĐƠN NHÁP', value: String(ENUM_ORDER_STATUS.DRAFT) },
  { label: 'ĐÃ XẾP CHUYẾN', value: String(ENUM_ORDER_STATUS.DISPATCHED) },
  { label: 'TRONG CHUYẾN', value: String(ENUM_ORDER_STATUS.IN_TRANSIT) },
  { label: 'HOÀN THÀNH', value: String(ENUM_ORDER_STATUS.FINISHED) },
  { label: 'ĐƠN ĐÃ HỦY', value: String(ENUM_ORDER_STATUS.CANCELLED) },
];

export default function OrdersFilter({ onCallback, loading }: { onCallback: (output: any) => void; loading: boolean }) {
  const { mutateAsync: getEntity, isPending: _loading } = useGetUserMeta();
  const { mutateAsync: updateEntity, isPending: updating } = useUpdateUserMeta();
  const { mutateAsync: deleteEntity } = useDeleteUserMeta();

  const key_for_saving_setting = `tab_orders_index_`;

  const getItem = useCallback(async (meta_key: string) => await getEntity(helpers.parseMetaKey(meta_key)), []);
  const removeItem = useCallback(async (meta_key: string) => await deleteEntity(helpers.parseMetaKey(meta_key)), []);
  const updateItem = useCallback(async (meta_key: string, meta_value: any) => {
    return await updateEntity({ meta_key: helpers.parseMetaKey(meta_key), meta_value });
  }, []);

  const rootSearchPath = window.location.search;
  const mainQuery: any = useMemo(() => helpers.ExtractUrl(rootSearchPath) || {}, [rootSearchPath]);
  const { query, sort, order_status, departure_date } = mainQuery;
  const money_collection_type = mainQuery['transactions.transaction_type'];
  const pickup_by = mainQuery['order_meta.pickup_by'];
  const dropoff_by = mainQuery['order_meta.dropoff_by'];

  const [sortSelected, setSortSelected] = useState<string[]>([helpers.colonToOldSort(sort) || 'createdAt, desc']);
  const [queryValue, setQueryValue] = useState(query || '');
  const [moneyCollectionType, setMoneyCollectionType] = useState(money_collection_type || '');
  const [orderStatus, setOrderStatus] = useState(order_status || '');
  const [pickupDriverID, setPickupDriverID] = useState(pickup_by || '');
  const [pickupDriverName, setPickupDriverName] = useState<string | null>(null);
  const [dropoffDriverID, setDropoffDriverID] = useState(dropoff_by || '');
  const [dropoffDriverName, setDropoffDriverName] = useState<string | null>(null);
  const [dateRanges, setDateRanges] = useState<any>(formatDateRange(departure_date));
  const [selected, setSelected] = useState(0);
  const [itemStrings, setItemStrings] = useState(['ALL']);

  const reloadDataDownFromURL = (mainQuery: any) => {
    const { query, order_status, departure_date } = mainQuery;
    const money_collection_type = mainQuery['transactions.transaction_type'];
    const pickup_by = mainQuery['order_meta.pickup_by'];
    const dropoff_by = mainQuery['order_meta.dropoff_by'];

    setQueryValue(query || '');
    setMoneyCollectionType(money_collection_type || '');
    setOrderStatus(order_status || '');
    setPickupDriverID(pickup_by || '');
    setPickupDriverName(null);
    setDropoffDriverID(dropoff_by || '');
    setDropoffDriverName(null);
    setDateRanges(formatDateRange(departure_date));
  };

  useEffect(() => {
    reloadDataDownFromURL(mainQuery);
  }, [mainQuery]);

  function getCurrentSetting(): string {
    return JSON.stringify(helpers.ExtractUrl(window.location.search) || false);
  }

  const onChangeMainQueryCallback = useMemo(() => helpers.debounce((_value) => onCallback?.call(this, _value), 200), []);
  const onChangeQueryCallback = useMemo(() => helpers.debounce((_value) => onCallback?.call(this, _value), 500), []);

  useDidMountEffect(() => {
    if (!sortSelected) return;
    const sortString = Array.isArray(sortSelected) ? sortSelected.join('') : sortSelected;
    onChangeMainQueryCallback({ sort: helpers.oldSortToColon(sortString) });
  }, [sortSelected]);

  useEffect(() => {
    getItem('orders_tabs')
      .then((response) => {
        if (helpers.isEmpty(response)) return;
        setItemStrings(response || []);
      })
      .catch((e) => console.log(e, 'e_orders_filter_tabs'));
  }, []);

  useDidMountEffect(() => {
    updateItem('orders_tabs', JSON.stringify(itemStrings));
  }, [itemStrings]);

  const changeTabFilterAction = useCallback(async (index: number, name: string) => {
    if (index === 0) {
      reloadDataDownFromURL({});
      return onChangeMainQueryCallback(false);
    }
    onChangeMainQueryCallback(false);
    await helpers.sleep(100);
    getItem(key_for_saving_setting + name)
      .then((response: any) => {
        if (!response) return;
        onChangeMainQueryCallback(response);
        reloadDataDownFromURL(response);
      })
      .catch((_) => {});
  }, []);

  const deleteView = (index: number, value: string) => {
    const newItemStrings = [...itemStrings];
    newItemStrings.splice(index, 1);
    setItemStrings(newItemStrings);
    setSelected(0);
    removeItem(key_for_saving_setting + value);
  };

  const duplicateView = async (name: string) => {
    setItemStrings([...itemStrings, name]);
    setSelected(itemStrings.length);
    updateItem(key_for_saving_setting + name, getCurrentSetting());
    return true;
  };

  const tabs: any[] =
    Array.isArray(itemStrings) &&
    itemStrings?.map((item, index) => ({
      content: item,
      index,
      onAction: () => changeTabFilterAction(index, item),
      id: `${item}-${index}`,
      isLocked: index === 0,
      actions:
        index === 0
          ? []
          : [
              {
                type: 'rename',
                onAction: () => {},
                onPrimaryAction: async (value: string): Promise<boolean> => {
                  setItemStrings(tabs?.map((item, idx) => (idx === index ? value : item.content)));
                  return true;
                },
              },
              { type: 'duplicate', onPrimaryAction: duplicateView },
              {
                type: 'delete',
                onPrimaryAction: async (value: string) => {
                  deleteView(index, value);
                  return true;
                },
              },
            ],
    }));

  const { mode, setMode } = useSetIndexFiltersMode();

  const handleFiltersQueryChange = useCallback((value: string) => {
    setQueryValue(value);
    onChangeQueryCallback({ query: value, page: 1 });
  }, []);

  const handleQueryValueRemove = useCallback(() => {
    setQueryValue('');
    onChangeQueryCallback({ query: '', page: 1 });
  }, []);

  const sortOptions: any[] = [
    { label: 'Ngày tạo', value: 'createdAt, asc', directionLabel: __('ascending') },
    { label: 'Ngày tạo', value: 'createdAt, desc', directionLabel: __('descending') },
    { label: 'Ngày đi', value: 'departure_date, asc', directionLabel: __('ascending') },
    { label: 'Ngày đi', value: 'departure_date, desc', directionLabel: __('descending') },
    { label: 'Tổng phí', value: 'total_fee, asc', directionLabel: __('ascending') },
    { label: 'Tổng phí', value: 'total_fee, desc', directionLabel: __('descending') },
  ];

  function optionLabel(options: { label: string; value: string }[], value: string): string {
    return options.find((option) => option.value === value)?.label || 'TẤT CẢ';
  }

  function disambiguateLabel(key: string, value: string | any): string {
    switch (key) {
      case 'keyword':
        return `Từ khóa: ${value}`;
      case 'money_collection_type':
        return `Công nợ: ${optionLabel(MONEY_COLLECTION_TYPE_OPTIONS, value)}`;
      case 'order_status':
        return `Tình trạng vé: ${optionLabel(ORDER_STATUS_OPTIONS, value)}`;
      case 'pickup_driver':
        return `Tài xế đón: ${pickupDriverName || value || 'Tất cả'}`;
      case 'dropoff_driver':
        return `Tài xế trả: ${dropoffDriverName || value || 'Tất cả'}`;
      case 'daterange': {
        const from = value[0] ? dayjs(new Date(Number(value[0]))).format('DD-MM-YYYY') : '-';
        const to = value[1] ? dayjs(new Date(Number(value[1]))).format('DD-MM-YYYY') : '-';
        return `Ngày đi: từ ${from} đến ${to}`;
      }
      default:
        return value as string;
    }
  }

  const handleMoneyCollectionTypeWithChange = useCallback((value: string) => {
    setMoneyCollectionType(value);
    onChangeMainQueryCallback({ 'transactions.payment_method': value, page: 1 });
  }, []);

  const handleMoneyCollectionTypeWithRemove = useCallback(() => {
    setMoneyCollectionType('');
    onChangeMainQueryCallback({ 'transactions.payment_method': '', page: 1 });
  }, []);

  const handleOrderStatusWithChange = useCallback((value: string) => {
    setOrderStatus(value);
    onChangeMainQueryCallback({ order_status: value, page: 1 });
  }, []);

  const handleOrderStatusWithRemove = useCallback(() => {
    setOrderStatus('');
    onChangeMainQueryCallback({ order_status: '', page: 1 });
  }, []);

  const handlePickupDriverWithChange = useCallback((value: string) => {
    setPickupDriverID(value);
    onChangeMainQueryCallback({ 'order_meta.pickup_by': value, page: 1 });
  }, []);

  const handlePickupDriverWithRemove = useCallback(() => {
    setPickupDriverID('');
    setPickupDriverName(null);
    onChangeMainQueryCallback({ 'order_meta.pickup_by': '', page: 1 });
  }, []);

  const handleDropoffDriverWithChange = useCallback((value: string) => {
    setDropoffDriverID(value);
    onChangeMainQueryCallback({ 'order_meta.dropoff_by': value, page: 1 });
  }, []);

  const handleDropoffDriverWithRemove = useCallback(() => {
    setDropoffDriverID('');
    setDropoffDriverName(null);
    onChangeMainQueryCallback({ 'order_meta.dropoff_by': '', page: 1 });
  }, []);

  const handleCalendarRemove = useCallback(() => {
    setDateRanges([]);
    onChangeMainQueryCallback({ departure_date: '', page: 1 });
  }, []);

  const handleFiltersClearAll = useCallback(() => {
    handleQueryValueRemove();
    handleMoneyCollectionTypeWithRemove();
    handleOrderStatusWithRemove();
    handlePickupDriverWithRemove();
    handleDropoffDriverWithRemove();
    handleCalendarRemove();
    onChangeMainQueryCallback(false);
  }, [
    handleQueryValueRemove,
    handleMoneyCollectionTypeWithRemove,
    handleOrderStatusWithRemove,
    handlePickupDriverWithRemove,
    handleDropoffDriverWithRemove,
    handleCalendarRemove,
    onChangeMainQueryCallback,
  ]);

  const [{ month, year }, setDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });

  const [{ startDate, endDate }, setDateRange] = useState({
    startDate: new Date().getTime(),
    endDate: new Date().getTime(),
  });

  function handleMonthChange(month: number, year: number): void {
    setDate({ month, year });
  }

  function handleCalendarChange({ start, end }): void {
    const normalizedStartDate = new Date(start);
    normalizedStartDate.setHours(0, 0, 0, 0);

    const normalizedEndDate = new Date(end || start);
    normalizedEndDate.setHours(23, 59, 59, 999);

    const startDate = normalizedStartDate.valueOf();
    const endDate = normalizedEndDate.valueOf();

    setDateRange({ startDate: start.valueOf(), endDate: end.valueOf() });
    setDateRanges([startDate, endDate]);
    onChangeMainQueryCallback({ departure_date: `gte:${startDate},lte:${endDate}`, page: 1 });
  }

  const filters = [
    {
      key: 'money_collection_type',
      label: 'Công nợ',
      filter: (
        <Select
          value={moneyCollectionType}
          label="Công nợ"
          labelInline
          onChange={handleMoneyCollectionTypeWithChange}
          options={MONEY_COLLECTION_TYPE_OPTIONS}
        />
      ),
      shortcut: true,
    },
    {
      key: 'order_status',
      label: 'Tình trạng vé',
      filter: <Select value={orderStatus} label="Tình trạng vé" labelInline onChange={handleOrderStatusWithChange} options={ORDER_STATUS_OPTIONS} />,
      shortcut: true,
    },
    {
      key: 'pickup_driver',
      label: 'Tài xế đón',
      filter: (
        <QuickSearchUser
          title=""
          noWrap
          placeholder="Tìm theo tên tài xế đón"
          user_role="driver"
          onClose={(user: TypedUser) => {
            handlePickupDriverWithChange(user.user_id);
            setPickupDriverName(user.display_name);
          }}
        />
      ),
      shortcut: true,
    },
    {
      key: 'dropoff_driver',
      label: 'Tài xế trả',
      filter: (
        <QuickSearchUser
          title=""
          noWrap
          placeholder="Tìm theo tên tài xế trả"
          user_role="driver"
          onClose={(user: TypedUser) => {
            handleDropoffDriverWithChange(user.user_id);
            setDropoffDriverName(user.display_name);
          }}
        />
      ),
      shortcut: true,
    },
    {
      key: 'daterange',
      label: 'Ngày đi',
      filter: (
        <DatePicker
          month={month}
          year={year}
          selected={{ start: new Date(startDate), end: new Date(endDate) }}
          onMonthChange={handleMonthChange}
          onChange={handleCalendarChange}
          multiMonth={false}
          allowRange
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters: IndexFiltersProps['appliedFilters'] = [];

  // if (!helpers.isEmpty(moneyCollectionType)) {
  appliedFilters.push({
    key: 'money_collection_type',
    label: disambiguateLabel('money_collection_type', moneyCollectionType),
    onRemove: handleMoneyCollectionTypeWithRemove,
  });
  // }

  // if (!helpers.isEmpty(orderStatus)) {
  appliedFilters.push({ key: 'order_status', label: disambiguateLabel('order_status', orderStatus), onRemove: handleOrderStatusWithRemove });

  appliedFilters.push({ key: 'pickup_driver', label: disambiguateLabel('pickup_driver', pickupDriverID), onRemove: handlePickupDriverWithRemove });

  appliedFilters.push({
    key: 'dropoff_driver',
    label: disambiguateLabel('dropoff_driver', dropoffDriverID),
    onRemove: handleDropoffDriverWithRemove,
  });
  // }

  appliedFilters.push({ key: 'daterange', label: disambiguateLabel('daterange', dateRanges), onRemove: handleCalendarRemove });

  const onCreateNewView = async (value: string) => {
    setItemStrings([...itemStrings, value]);
    setSelected(itemStrings.length);
    updateItem(key_for_saving_setting + value, getCurrentSetting());
    return true;
  };

  const onHandleSave = useCallback(async () => {
    if (!itemStrings[selected]) return;
    updateItem(key_for_saving_setting + itemStrings[selected], getCurrentSetting());
    return true;
  }, [selected, itemStrings]);

  const primaryActionForFilterFields: IndexFiltersProps['primaryAction'] =
    selected === 0
      ? { type: 'save-as', onAction: onCreateNewView, disabled: false, loading: false }
      : { type: 'save', onAction: onHandleSave, disabled: false, loading: false };

  return (
    <>
      <IndexFilters
        loading={loading || _loading || updating}
        sortOptions={sortOptions}
        sortSelected={sortSelected}
        queryValue={queryValue}
        queryPlaceholder="Tìm kiếm"
        onQueryChange={handleFiltersQueryChange}
        onQueryClear={handleQueryValueRemove}
        onSort={setSortSelected}
        primaryAction={primaryActionForFilterFields}
        cancelAction={{ onAction: () => {}, disabled: false, loading: false }}
        tabs={tabs}
        selected={selected}
        onSelect={setSelected}
        canCreateNewView={true}
        onCreateNewView={onCreateNewView}
        onClearAll={handleFiltersClearAll}
        mode={mode}
        setMode={setMode}
        filters={filters}
        appliedFilters={appliedFilters}
      />
      {mode === 'DEFAULT' ? (
        <div className="Polaris-Filters__FiltersWrapper Polaris-Filters__FiltersWrapperWithAddButton filterWrapper">
          <div className="Polaris-Filters__FiltersInner">
            {!helpers.isEmpty(queryValue) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('keyword', queryValue)}
              </span>
            ) : null}
            {!helpers.isEmpty(moneyCollectionType) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('money_collection_type', moneyCollectionType)}
              </span>
            ) : null}
            {!helpers.isEmpty(orderStatus) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('order_status', orderStatus)}
              </span>
            ) : null}
            {!helpers.isEmpty(pickupDriverID) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('pickup_driver', pickupDriverID)}
              </span>
            ) : null}
            {!helpers.isEmpty(dropoffDriverID) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('dropoff_driver', dropoffDriverID)}
              </span>
            ) : null}
            {!helpers.isEmpty(dateRanges) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('daterange', dateRanges)}
              </span>
            ) : null}
            {helpers.isEmpty(queryValue) &&
            helpers.isEmpty(moneyCollectionType) &&
            helpers.isEmpty(orderStatus) &&
            helpers.isEmpty(pickupDriverID) &&
            helpers.isEmpty(dropoffDriverID) &&
            helpers.isEmpty(dateRanges) ? (
              <InlineStack blockAlign="center" align="start" wrap={false}>
                <Link removeUnderline onClick={() => setMode('FILTERING' as any)}>
                  {'   '}{' '}
                  <Text as="span" variant="bodySm" tone="subdued">
                    Mở bộ lọc (hoặc bấm nút F)
                  </Text>
                </Link>
              </InlineStack>
            ) : (
              <Button variant="plain" size="micro" onClick={handleFiltersClearAll}>
                xóa hết
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
